import React, { useEffect, useState, useRef } from "react";
import { CircularProgress } from "@mui/material";
import Swal from "sweetalert2";
import DynamicForm from "../../../data/Axios/DynamicForm";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

import {
  companyManagementUrl,
  salesManagement,
  setupManagementUrl,
} from "../../../config";
import {
  branchFields,
  companyFields,
} from "../../../data/DynamicTable/CompanyManagementForms";
import {
  allCompanies,
  AllProducts,
  allRegions,
  allSubscriptionPlans,
  AllVendors,
  UOMQuery,
} from "../../../data/Axios/queries";

const base_url = setupManagementUrl.uri;
const Sale_base_url = salesManagement.uri;

const PurchaseOrderItemsForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  // Get PurchaseOrderData from localStorage
  const purchaseOrderData = JSON.parse(
    localStorage.getItem("PurchaseOrderData")
  );

  const deliveryStatus = purchaseOrderData?.deliveryStatus;
  const paymentStatus = purchaseOrderData?.paymentStatus;

  const [productsData, setproductsData] = useState([]);
  const [vendorData, setvendorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(true);
  const [uomData, setUomData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [isEditRestricted, setIsEditRestricted] = useState(false);

  // ------------------------------------------------------------
  // Guard used to make sure the "Edit Not Allowed" alert (for
  // items that have already been distributed) only ever fires
  // once per mount, even if the effect below re-runs (e.g. React
  // Strict Mode double-invoking effects in dev, or a parent
  // re-render that doesn't actually change the underlying data).
  // ------------------------------------------------------------
  const hasShownEditBlockedAlertRef = useRef(false);

  // ------------------------------------------------------------
  // Helper: determine whether the item has already been
  // distributed to either retail or warehouse.
  // Supports true, 1, "1", and "true".
  // ------------------------------------------------------------
  const isDistributed = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).toLowerCase() === "true"
    );
  };

  // ------------------------------------------------------------
  // Check if editing is allowed
  // ------------------------------------------------------------
  const checkEditAllowed = () => {
    if (!props.isEditing) {
      return {
        allowed: true,
        restricted: false,
      };
    }

    const isRetailDistributed = isDistributed(
      props.data?.isRetailDistributed
    );

    // const isWarehouseDistributed = isDistributed(
    //   props.data?.isWarehouseDistributed
    // );

    // Once distributed, editing is completely prohibited.
    if (isRetailDistributed) {
      return {
        allowed: false,
        restricted: false,
        errorMessage:
          "This purchase order item cannot be edited because the items have already been distributed.",
      };
    }

    // Existing delivery restriction
    if (deliveryStatus === 1) {
      return {
        allowed: true,
        restricted: true,
        errorMessage: "Delivery is pending. Only shoe sizes can be edited.",
      };
    }

    return {
      allowed: true,
      restricted: false,
    };
  };

  useEffect(() => {
    let timeoutId;

    if (props.isEditing) {
      const { allowed, restricted, errorMessage } = checkEditAllowed();

      setIsEditRestricted(restricted);

      // ----------------------------------------------------------
      // Distributed item:
      // Show error only.
      // Do NOT render the edit form.
      // Do NOT close the parent modal.
      // ----------------------------------------------------------
      if (!allowed) {
        setShowForm(false);
        setLoading(false);

        // Only ever show this alert once, even if this effect
        // re-runs for the same "not allowed" item.
        if (!hasShownEditBlockedAlertRef.current) {
          hasShownEditBlockedAlertRef.current = true;

          timeoutId = setTimeout(() => {
            Swal.fire({
              icon: "error",
              title: "Edit Not Allowed",
              text:
                errorMessage || "This purchase order item cannot be edited.",
              confirmButtonText: "OK",
            });
          }, 100);
        }

        return () => {
          if (timeoutId) clearTimeout(timeoutId);
        };
      }

      setShowForm(true);
    }

    async function fetchAllData() {
      try {
        setLoading(true);

        const [
          productsDataResponse,
          vendorDataResponse,
          uomDataResponse,
          colorDataResponse,
        ] = await Promise.all([
          graphqlQuery(AllProducts, base_url),
          graphqlQuery(AllVendors, Sale_base_url),
          graphqlQuery(UOMQuery, setupManagementUrl.uri),
        ]);

        if (productsDataResponse) {
          setproductsData(productsDataResponse);
        }

        if (vendorDataResponse) {
          setvendorData(vendorDataResponse);
        }

        if (Array.isArray(uomDataResponse)) {
          setUomData(uomDataResponse);
        }

        if (Array.isArray(colorDataResponse)) {
          setColorData(colorDataResponse);
        }
      } catch (error) {
        console.error("Error fetching data:", error);

        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to load form data. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    }

    if (showForm) {
      fetchAllData();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    props.isEditing,
    props.data?.id,
    props.data?.isRetailDistributed,
    props.data?.isWarehouseDistributed,
    deliveryStatus,
    paymentStatus,
  ]);

  // Prepare UOM options
  const uomOptions = uomData.map((u) => ({
    value: u.id,
    label: `${u.name} (${u.unit})`,
  }));

  // Prepare color options
  const colorOptions = colorData.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  // Enhanced initialValues that maps all form fields to the fetched data
  const initialValues = {
    vendorId: props.data ? props.data.vendorId : "",
    productId: props.data ? props.data.productId : "",

    quantity:
      props.data.cartons && props.data.quantityPerCarton
        ? parseFloat(props.data.cartons) *
          parseFloat(props.data.quantityPerCarton)
        : 0,

    expiryDate:
      props.data && props.data.expiryDate
        ? (() => {
            // use local date parts — toISOString() returns UTC
            // which can shift date by -1 in EAT (UTC+3)
            const d = new Date(props.data.expiryDate);
            const pad = (n) => String(n).padStart(2, "0");

            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
              d.getDate()
            )}`;
          })()
        : "",

    productCode: props.data ? props.data.productCode : "",
    aliasProductName: props.data ? props.data.aliasProductName : "",
    productName: props.data ? props.data.productName : "",
    vendorName: props.data ? props.data.vendorName : "",
    vendorComments: props.data ? props.data.vendorComments : "",
    cartons: props.data ? props.data.cartons : "",
    quantityPerCarton: props.data ? props.data.quantityPerCarton : "",
    amount: props.data ? props.data.amount : "",

    stockDueDate:
      props.data && props.data.stockDueDate
        ? (() => {
            // use local date parts — toISOString() returns UTC
            // which can shift date by -1 in EAT (UTC+3)
            const d = new Date(props.data.stockDueDate);
            const pad = (n) => String(n).padStart(2, "0");

            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
              d.getDate()
            )}`;
          })()
        : "",

    unitOfMeasure: props.data ? props.data.unitOfMeasure : "",
    minShoeSize: props.data ? props.data.minShoeSize : "",
    maxShoeSize: props.data ? props.data.maxShoeSize : "",
    isExternalWarehouse: props.data ? props.data.isExternalWarehouse : false,

    // Existing fields
    uomId: props.data ? props.data.uomId : "",
    buyingPricePerCarton: props.data ? props.data.buyingPricePerCarton : "",
    sellingPricePerCarton: props.data ? props.data.sellingPricePerCarton : "",
    buyingPricePerPair: props.data ? props.data.buyingPricePairs : "",
    sellingPricePerPair: props.data ? props.data.sellingPricePairs : "",
    reorderPoint: props.data ? props.data.reorderPoint : "",
    colorId: props.data ? props.data.colorId : "",
    productAliasName: props.data ? props.data.productAliasName : "",
    vendorBranchId: props.data ? props.data.vendorBranchId : "",
    warehouseInventoryId: props.data ? props.data.warehouseInventoryId : "",
  };

  const productsOptions = productsData.map((product) => ({
    value: product.id,
    label: product.productName,
  }));

  const vendorOptions = vendorData.map((vendor) => ({
    value: vendor.id,
    label: vendor.vendorName,
  }));

  // Create a base fields array
  const baseFields = [
    {
      name: "vendorId",
      label: "Vendor",
      type: "select",
      gridColumn: "span 2",
      options: vendorOptions,
      required: true,
    },
    {
      name: "productId",
      label: "Product/Item",
      type: "select",
      gridColumn: "span 2",
      options: productsOptions,
      required: true,
    },
    {
      name: "productCode",
      label: "Product Code",
      type: "text",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "aliasProductName",
      label: "Product Alias Name",
      type: "text",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "quantity",
      label: "Quantity *",
      type: "disabled",
      gridColumn: "span 2",
      disabled: true,
      hidden: false,
    },
    {
      name: "cartons",
      label: "Cartons *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "quantityPerCarton",
      label: "Quantity Per Carton *",
      type: "number",
      gridColumn: "span 2",
      required: true,
    },
    {
      name: "uomId",
      label: "Unit of Measure",
      type: "select",
      gridColumn: "span 2",
      options: uomOptions,
      required: true,
    },
    {
      name: "buyingPricePerCarton",
      label: "Buying Price/Carton",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "sellingPricePerCarton",
      label: "Selling Price/Carton",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "buyingPricePerPair",
      label: "Buying Price/Pair",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "sellingPricePerPair",
      label: "Selling Price/Pair",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "minShoeSize",
      label: "Min Shoe Size",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "maxShoeSize",
      label: "Max Shoe Size",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "reorderPoint",
      label: "Reorder Point",
      type: "number",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "stockDueDate",
      label: "Stock Due Date",
      type: "date",
      gridColumn: "span 2",
      required: false,
    },
    {
      name: "vendorComments",
      label: "Vendor Comments",
      type: "text",
      gridColumn: "span 4",
      multiline: true,
      rows: 3,
      required: false,
    },
  ];

  // Create a fields array that changes based on editing restrictions
  const getFields = () => {
    // If creating new item or not in restricted edit mode,
    // return all fields
    if (!props.isEditing || !isEditRestricted) {
      return baseFields;
    }

    // If in restricted edit mode, return only allowed fields
    return baseFields.filter((field) =>
      [
        "cartons",
        "quantityPerCarton",
        "quantity",
        "stockDueDate",
        "minShoeSize",
        "maxShoeSize",
      ].includes(field.name)
    );
  };

  // Get the fields to display
  const fields = getFields();

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // ----------------------------------------------------------
      // Double-check edit permission before submitting.
      // This protects against bypassing the UI restriction.
      // ----------------------------------------------------------
      if (props.isEditing) {
        const { allowed, errorMessage } = checkEditAllowed();

        if (!allowed) {
          setSubmitting(false);

          Swal.fire({
            icon: "error",
            title: "Edit Not Allowed",
            text:
              errorMessage || "This purchase order item cannot be edited.",
            confirmButtonText: "OK",
          });

          return;
        }
      }

      const mutationData = {
        id: props.data?.id || 0,

        vendorId: values.vendorId || props.data?.vendorId,

        companyId: companyId,

        isActive: true,

        productId: values.productId || props.data?.productId,

        productCode: values.productCode,

        expiryDate: values.expiryDate
          ? `${values.expiryDate}T00:00:00`
          : null,

        purchaseOrderId: props.data?.purchaseOrderId,

        quantity:
          values.cartons && values.quantityPerCarton
            ? parseFloat(values.cartons) * parseFloat(values.quantityPerCarton)
            : 0,

        vendorComments: values.vendorComments,

        cartons: values.cartons ? parseFloat(values.cartons) : null,

        quantityPerCarton: values.quantityPerCarton
          ? parseFloat(values.quantityPerCarton)
          : null,

        amount: values.amount ? parseFloat(values.amount) : null,

        stockDueDate: values.stockDueDate
          ? `${values.stockDueDate}T00:00:00`
          : null,

        aliasProductName: values.aliasProductName,

        uomId: values.uomId,

        buyingPricePerCarton: values.buyingPricePerCarton
          ? parseFloat(values.buyingPricePerCarton)
          : null,

        sellingPricePerCarton: values.sellingPricePerCarton
          ? parseFloat(values.sellingPricePerCarton)
          : null,

        buyingPricePairs: values.buyingPricePerPair
          ? parseFloat(values.buyingPricePerPair)
          : null,

        sellingPricePairs: values.sellingPricePerPair
          ? parseFloat(values.sellingPricePerPair)
          : null,

        reorderPoint: values.reorderPoint
          ? parseFloat(values.reorderPoint)
          : null,

        colorId: values.colorId,

        productAliasName: values.productAliasName,

        vendorBranchId: values.vendorBranchId,

        warehouseInventoryId: values.warehouseInventoryId,

        minShoeSize: values.minShoeSize
          ? parseFloat(values.minShoeSize)
          : null,

        maxShoeSize: values.maxShoeSize
          ? parseFloat(values.maxShoeSize)
          : null,

        isExternalWarehouse:
          values.isExternalWarehouse || props.data?.isExternalWarehouse || 0,
      };

      // ----------------------------------------------------------
      // If editing is restricted, only submit the existing item
      // with the permitted shoe-size-related changes.
      // ----------------------------------------------------------
      if (props.isEditing && isEditRestricted) {
        const restrictedData = {
          ...mutationData,

          // Explicitly ensure critical IDs are present
          // from props.data if values missed them.
          vendorId: mutationData.vendorId || props.data?.vendorId,

          productId: mutationData.productId || props.data?.productId,

          uomId: mutationData.uomId || props.data?.uomId,
        };

        // Remove null values
        Object.keys(restrictedData).forEach((key) => {
          if (
            restrictedData[key] === null ||
            restrictedData[key] === undefined ||
            restrictedData[key] === ""
          ) {
            delete restrictedData[key];
          }
        });

        const response = await generateAndExecuteMutation(
          "updatePurchaseOrderItem",
          restrictedData,
          "updatedPurchaseOrderItem",
          Sale_base_url
        );

        if (response && Object.values(response)[0] != null) {
          props.onClose();
          props.onAction();

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Shoe sizes updated successfully.",
          });
        }
      } else {
        // Normal submission for all other cases

        // Remove null or undefined values
        Object.keys(mutationData).forEach((key) => {
          if (
            mutationData[key] === null ||
            mutationData[key] === undefined ||
            mutationData[key] === ""
          ) {
            delete mutationData[key];
          }
        });

        const response = await generateAndExecuteMutation(
          props.isEditing
            ? "updatePurchaseOrderItem"
            : "createPurchaseOrderItem",

          mutationData,

          props.isEditing
            ? "updatedPurchaseOrderItem"
            : "createdPurchaseOrderItem",

          Sale_base_url
        );

        if (response && Object.values(response)[0] != null) {
          props.onClose();
          props.onAction();

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Purchase order item ${
              props.isEditing ? "updated" : "created"
            } successfully.`,
          });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  // Don't show anything if loading
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  // Don't render the form if editing is not allowed
  if (!showForm) {
    return null;
  }

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      onSubmit={handleSubmit}
      title={
        props.isEditing
          ? "Edit Purchase Order Item" +
            (isEditRestricted ? " (Limited Editing)" : "")
          : "Create Purchase Order Item"
      }
      subtitle={
        props.isEditing
          ? isEditRestricted
            ? "Cartons, quantity per carton, stock due date and shoe sizes can be edited when delivery is pending"
            : "Edit an existing purchase order item"
          : "Create a new purchase order item"
      }
      isEditing={props.isEditing}
    />
  );
};

export default PurchaseOrderItemsForm;